import { yupResolver } from '@hookform/resolvers/yup'
import DeleteIcon from '@mui/icons-material/Delete'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import { SocketClient } from 'app/socketIO'
import DeleteDialog from 'components/DeleteDialog'
import RichTextEditor from 'components/RichTextEditor'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'

interface FormInput {
  date: number
  tag: string
  title: string
  content: string
}
export default function NewsDetail() {
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const schema = yup.object({
    tag: yup.string().required('タグを入力してください。'),
    title: yup.string().required('タイトルを入力してください。'),
    content: yup.string().required('内容を入力してください。'),
  })
  const news = useAppSelector((s) =>
    s.News.list.find((f) => f.id === Number(id))
  )
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: news,
  })
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (id && (!news || !news.active)) {
      navigate('/news')
    }
    SocketClient.socket?.emit('lock', {
      model: 'news',
      id: Number(id),
    })
    return () => {
      SocketClient.socket?.emit('unlock', {
        model: 'news',
        id: Number(id),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: '新着情報管理',
          url: '/news',
        },
        {
          text: '詳細',
          url: '/news/' + id,
        },
      ])
    )
  }, [dispatch, id])
  const onSubmitForm: SubmitHandler<FormInput> = async (data) => {
    try {
      setLoading(true)
      await axiosClient.put('/api/news/' + id, data)

      enqueueSnackbar('新規変更が完了しました。', {
        variant: 'success',
      })
      navigate('/news')
    } catch (error) {
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid container spacing={1} mt={2}>
      {showDelete && (
        <DeleteDialog
          open={showDelete}
          handleClose={() => setShowDelete(false)}
          apiEndpoint={'/api/news/' + id}
          handleFinal={() => navigate('/news')}
        >
          削除してもよろしいでしょうか
        </DeleteDialog>
      )}
      <Grid item xs={12}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderBottom: 2 }}
        >
          <Typography variant="h5" component="h2" fontWeight="bold">
            詳細情報
          </Typography>
          <Button
            variant="contained"
            size="small"
            component="label"
            color="secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 4,
              transform: 'translateY(-0.4rem)',
            }}
            onClick={() => setShowDelete(true)}
          >
            <DeleteIcon />
            <Typography variant="caption" ml={0.4} mt={0.4}>
              削除
            </Typography>
          </Button>
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <Container
          disableGutters
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            margin: '0',
            padding: '0 !important',
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            position: 'relative',
          }}
          onSubmit={handleSubmit(onSubmitForm)}
        >
          <DatePicker
            views={['year', 'month', 'day']}
            label="日付 *"
            inputFormat="yyyy年MM月dd日"
            value={new Date(getValues('date'))}
            onChange={(date) => {
              if (date && date instanceof Date && !isNaN(date.valueOf())) {
                setValue('date', date.getTime(), {
                  shouldValidate: true,
                })
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                fullWidth
                sx={{
                  maxWidth: 380,
                }}
              />
            )}
          />
          <TextField
            required
            label="タグ"
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            {...register('tag')}
            error={'tag' in errors}
            helperText={errors.tag?.message}
          />
          <TextField
            required
            label="タイトル"
            margin="dense"
            fullWidth
            size="small"
            variant="outlined"
            {...register('title')}
            error={'title' in errors}
            helperText={errors.title?.message}
          />
          <Typography>内容</Typography>
          <RichTextEditor
            value={getValues('content')}
            setValue={(val) => setValue('content', val)}
          />
          <Stack mt={4}>
            <LoadingButton
              loading={loading}
              disabled={Object.keys(errors).length > 0}
              size="small"
              color="secondary"
              variant="contained"
              sx={{
                borderRadius: 2,
                width: 200,
                fontWeight: 600,
                fontSize: '1rem',
              }}
              type="submit"
            >
              変更する
            </LoadingButton>
          </Stack>
        </Container>
      </Grid>
    </Grid>
  )
}
